import { Show } from 'solid-js';
import clsx from 'clsx';
import { ImageWordpress } from '../../types/shared';
import { ImageComponent } from '../image-component/image-component';
import { CardFavouriteButton, CardFavouriteTarget } from '../my-content/card-favourite-button';
import { VideoImageBeforeCookiesAccepted } from '../video-embed/video-image-before-cookie';
import { PlayAndCircle } from '../../ui-components/play-and-circle/play-and-circle';
import decodeHtmlEntities from '../../tools/decode-html-entities';

/* Widest band is the 1-and-2-card views (~660px measured at the 1440 breakpoint), not the
   3-card view. ImageComponent also fetches a 2x render, so both 750 and 1500 have to be in the
   image service's ALLOWED_SIZES — they are. */
const IMAGE_WIDTH = 750;

/* Beats the global `.keen-slider__slide` height/overflow so flex can level card
   heights and the shadow isn't clipped. Pairs with `allowSlideShadow` on Slider. */
export const SLIDE_STRETCH = 'h-auto! overflow-visible!';

export type SliderCardMediaFit = 'cover' | 'contain';

type SliderCardMediaProps = {
    image?: ImageWordpress;
    /** Photos crop to the box; product cut-outs are contained so nothing is lost. */
    fit?: SliderCardMediaFit;
    /** Poster fallback for a video card that has no image of its own. */
    videoUrl?: string;
    showPlayOverlay?: boolean;
    categoryLabel?: string;
    myContent?: CardFavouriteTarget;
};

/* One media band for every card type in the slider: editors attach images at any
   ratio, so the box is fixed and the image adapts to it.

   5/4 rather than 29/16: at 29/16, 56 of the 905 live card images lost more than
   half their height to the cover crop. At 5/4 none do. */
export const SliderCardMedia = (props: SliderCardMediaProps) => {
    /* WordPress hands us `altText` on some post types and `alt` on others. */
    const altText = () => props.image?.altText || props.image?.alt || '';

    const isContained = () => props.fit === 'contain';

    return (
        <div class="relative aspect-[5/4] w-full shrink-0 overflow-hidden">
            <Show when={props.image?.url}>
                <div class={clsx('absolute inset-0', isContained() && 'flex items-center p-cvi-6')}>
                    <ImageComponent
                        src={props.image!.url}
                        height={'AUTO'}
                        width={IMAGE_WIDTH}
                        ratio={5 / 4}
                        fit={isContained() ? 'contain' : 'cover'}
                        altText={altText()}
                        roundCorners={false}
                        spacer={false}
                    />
                </div>
            </Show>

            {/* Draws its own play button and logo fallback, so our overlay stays off. */}
            <Show when={!props.image?.url && props.videoUrl}>
                <VideoImageBeforeCookiesAccepted videoUrl={props.videoUrl!} image={props.image} />
            </Show>

            <Show when={props.showPlayOverlay && props.image?.url}>
                <div class="absolute inset-0 flex items-center justify-center">
                    <PlayAndCircle />
                </div>
            </Show>

            <Show when={props.categoryLabel}>
                <span class="absolute right-cvi-6 top-cvi-6 z-[2] rounded-cvi-sm bg-cvi-bg-interactive-strong px-cvi-4 py-cvi-2 label-sm font-cvi-medium text-cvi-fg-on-color">
                    {decodeHtmlEntities(props.categoryLabel)}
                </span>
            </Show>

            <Show when={props.myContent}>
                <CardFavouriteButton
                    class="absolute right-cvi-6 bottom-cvi-6 z-20"
                    title={props.myContent!.title}
                    permalink={props.myContent!.permalink}
                    type={props.myContent!.type}
                />
            </Show>
        </div>
    );
};
